import {Form} from "../../model/form/form";
import FormGroup from "../../model/form/formGroup";

type TProgress = {
    lastVisitedForm: Form|null
    current: number,
    total: number,
    finished: boolean,
    inProgress: boolean,
}

export type TChapter = {
    name: string;
    progress: TProgress,
    orderedForms: Form[],
};

/**
 * Construct information for chapters
 *
 * Expected structure
 * 📁 Some form group (no rotation)
 * |- 📄 Chapter form (form from constructor)
 * |- 📁 Chapters form group (rotate as you wish)
 *    |- 📁 Chapter 1
 *    |- 📁 Chapter 2
 *    |- 📁 Chapter ...n
 *       |- ...any structure with forms in chapter
 */
export default class ChaptersInfo {

    /**
     * Ordered chapters with its state and forms
     */
    public readonly chapters: TChapter[];

    /**
     * Form that should be opened after chapters are completed
     */
    public readonly afterForm: Form | null;

    public readonly lastFinishedForm: Form;

    private readonly chapterFormGroup: FormGroup;
    private readonly orderedForms: Form[];
    private readonly finishedForms: Set<string>;
    private readonly jumpsToChapterForm: Set<string>;

    constructor(
        public readonly chapterForm: Form,
    ) {
        this.finishedForms = new Set(this.chapterForm.questionary.state.finishedForms);

        this.chapterFormGroup = this.getChaptersFormGroup();
        this.orderedForms = this.getOrderedForms();
        this.afterForm = this.getFormAfterChapter();
        this.jumpsToChapterForm = this.getJumpsToChapterForm();

        this.chapters = this.getChapters();

        this.lastFinishedForm = this.getLastFinishedForm();
    }

    public get lastForm(): Form {
        return this.orderedForms[this.orderedForms.length - 1];
    }

    public get nextForm(): Form | null {
        const nextFormId = this.getNextFormId(this.lastFinishedForm);
        return nextFormId ? this.chapterForm.questionary.formsById[nextFormId] : null;
    }


    private getChaptersFormGroup(): FormGroup {
        const parent = this.chapterForm.parent;
        if (!parent) {
            throw new Error('ChaptersForm must be in group with chapters FormGroup.');
        }

        const chapterFormIndex = parent.children.indexOf(this.chapterForm);
        const chapterGroup = parent.children[chapterFormIndex+1];

        if (!chapterGroup || !(chapterGroup instanceof FormGroup)) {
            throw new Error('Chapters FormGroup is not immediately following ChaptersForm.');
        }

        return chapterGroup;
    }

    private getOrderedForms(): Form[] {
        const formsById = this.chapterFormGroup.getFormsRecursiveById();
        formsById[this.chapterForm.id] = this.chapterForm;

        const orderedForms: Form[] = [];

        let currentForm: Form|null = this.chapterForm;
        while (currentForm) {
            orderedForms.push(currentForm);
            const nextFormId = this.getNextFormId(currentForm);
            currentForm = nextFormId ? formsById[nextFormId] : null;
        }

        return orderedForms;
    }

    private getNextFormId(form: Form): string|null {
        return form.questionary.randomizer!.translateMove(form, form.nextFormId || null) ?? null;
    }

    private getFormAfterChapter(): Form|null {
        const formId = this.getNextFormId(this.lastForm);

        if (!formId) {
            return null;
        }

        return this.chapterForm.questionary.formsById[formId] ?? null;
    }

    private getJumpsToChapterForm(): Set<string> {
        const chapterFormId = this.chapterForm.id;
        const resultArr = this.chapterForm.questionary.state.filterGoToJumps.filter(jump => jump[1] === chapterFormId)
            .map(jump => jump[0]);

        return new Set(resultArr);
    }

    private readChapterGroup(formGroup: FormGroup): TChapter {

        const orderedForms = this.getOrderFormsFromGroup(formGroup);
        const progress = this.getProgress(orderedForms);

        return {
            name: formGroup.name,
            orderedForms,
            progress,
       }
    }

    private getOrderFormsFromGroup(formGroup: FormGroup): Form[] {
        const formsById = formGroup.getFormsRecursiveById();

        return this.orderedForms.filter(form => formsById[form.id]);
    }

    private getProgress(orderedChapterForms: Form[]): TProgress {
        let current = 0;
        let lastVisitedForm: Form|null = null;

        for (let i = orderedChapterForms.length - 1; i >= 0; i--) {
            const form = orderedChapterForms[i];
            if (this.finishedForms.has(form.id)) {
                current = i+1;
                lastVisitedForm = form;
                break;
            }
        }

        const finished = (
            // assumed as finished - form of chapter is finished
            current === orderedChapterForms.length

            // assumed as finished - found executed filter action GoToForm from chapter back to chapterForm
            || orderedChapterForms.some(form => this.jumpsToChapterForm.has(form.id))
        );


        return {
            total: orderedChapterForms.length,
            current: finished ? orderedChapterForms.length : current,
            finished,
            inProgress: !finished && current !== 0,
            lastVisitedForm,
        }
    }

    private getChapters(): TChapter[] {
        return this.getChaptersFormGroup().children
            .filter(formItem => formItem instanceof FormGroup)
            .map(formGroup => this.readChapterGroup(<FormGroup>formGroup))
            .filter(chapter => chapter.orderedForms.length > 0)
            .sort((a,b) => this.orderedForms.indexOf(a.orderedForms[0]) - this.orderedForms.indexOf(b.orderedForms[0]));
    }

    private getLastFinishedForm(): Form {
        let lastForm = this.chapterForm;
        for (let i = this.orderedForms.length - 1; i >= 0; i--) {
            const form = this.orderedForms[i];
            if (this.finishedForms.has(form.id)) {
                lastForm = form;
                break;
            }
        }

        return lastForm;
    }

}
